import { ButtonV3, ModalV3, TypographyV3 } from '@jouzen/ecom-components';
import { useFeatureGate } from '@statsig/react-bindings';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import { useWizardStateContext } from '@/app/[locale]/product/_contexts/WizardStateProvider';

import BundleFixedFooter from './BundleFixedFooter';
import BundleUsSection from './BundleUsSection';
import CartIsFullMessage from './CartIsFullMessage';
import HSAFSAContent from './HSAFSAContent';
import OneYearMembershipSelector from './OneYearMembershipSelector';
import type { TitleText, TypographyColor, UseBundleStepReturn } from './types';
import useBundleStep from './useBundleStep';

const BundleStep = () => {
  const {
    isWarrantyAvailable,
    isModalOpen,
    modalButtonText,
    onOpenModal,
    setIsModalOpen,
  }: UseBundleStepReturn = useBundleStep();
  const t = useTranslations();
  const {
    hasMaxRingCount,
    isAddingToCart,
    nextButtonDisabled,
    ringCount,
    stockStatus,
  } = useWizardStateContext();
  const isUS = useFeatureGate('usa_only').value;

  const titleText: TitleText = hasMaxRingCount
    ? 'cart_is_full'
    : 'bundle_modal_title';
  const typographyColor: TypographyColor = hasMaxRingCount
    ? 'disabled'
    : 'dark';
  const size = isWarrantyAvailable ? 'large' : 'small';

  return (
    <>
      <ModalV3
        fixedFooter={
          <BundleFixedFooter
            hasMaxRingCount={hasMaxRingCount}
            ringCount={ringCount}
            modalButtonText={modalButtonText}
            nextButtonDisabled={nextButtonDisabled}
            isAddingToCart={isAddingToCart}
            stockStatus={stockStatus}
          />
        }
        isOpen={isModalOpen}
        className={cx(
          'md:rounded-xl',
          !isWarrantyAvailable
            ? 'md:ui-w-10/12 md:ui-max-h-[83%]'
            : 'md:ui-w-1/2 md:ui-max-h-1/2',
        )}
        size={size}
        variant="lightest"
        onOpenChange={(isOpen) => setIsModalOpen(isOpen)}
      >
        <TypographyV3 className="pb-3 text-4xl font-medium md:pb-8">
          {t(titleText)}
        </TypographyV3>
        <CartIsFullMessage />
        <HSAFSAContent />
        <div className="pt-5 pb-1 md:flex md:justify-between md:pt-8 md:pb-4">
          <div
            className={cx(
              'pb-3 md:border-b-[0px] md:pb-1',
              isWarrantyAvailable
                ? 'border-b md:w-[46%]'
                : 'border-b-0 md:w-full',
              hasMaxRingCount && isWarrantyAvailable
                ? 'border-gray-300/50'
                : 'border-sandstone-500/50',
            )}
            data-cy="bundle-membership-section"
          >
            <TypographyV3
              className="pb-3 text-xl font-medium md:pb-4"
              color={typographyColor}
            >
              {t('bundle_modal_one_year_title')}
            </TypographyV3>
            <TypographyV3
              className="pb-5 text-base font-medium"
              color={typographyColor}
              data-cy="bundle-membership-subtitle"
            >
              {t(
                isUS
                  ? 'bundle_modal_one_year_subtitle_hsa'
                  : 'bundle_modal_one_year_subtitle',
              )}
            </TypographyV3>
            <OneYearMembershipSelector />
            <TypographyV3 className="mt-6 italic">
              {t('add_ons_one_year_prepaid_copy')}
            </TypographyV3>
          </div>
          {isWarrantyAvailable && <BundleUsSection />}
        </div>
      </ModalV3>
      <ButtonV3
        data-cy="button-open-bundle-modal"
        disabled={nextButtonDisabled}
        loading={isAddingToCart}
        loadingTextEnabled
        size="large"
        type="submit"
        onClick={onOpenModal}
      >
        {t('pdp_add_to_cart')}
      </ButtonV3>
    </>
  );
};
export default BundleStep;
